// components/common/DateRangePicker.tsx
import React from "react";
import DatePicker from "react-datepicker";
import { FiCalendar } from "react-icons/fi";
import "react-datepicker/dist/react-datepicker.css";

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartChange: (date: Date | null) => void;
  onEndChange: (date: Date | null) => void;
  className?: string;
  containerClassName?: string;
  inputClassName?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  dateFormat?: string;
  showIcons?: boolean;
  separator?: string;
  disabled?: boolean;
  maxDate?: Date;
  minDate?: Date;
  size?: 'sm' | 'md' | 'lg';
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  className = "",
  containerClassName = "",
  inputClassName = "",
  startPlaceholder = "Start date",
  endPlaceholder = "End date",
  dateFormat = "MM/dd/yy",
  showIcons = true,
  separator = "to",
  disabled = false,
  maxDate = new Date(),
  minDate,
  size = 'md'
}) => {
  // Handle start date change with validation
  const handleStartChange = (date: Date | null) => {
    if (date && endDate && date > endDate) {
      // If start date is after end date, adjust end date
      onEndChange(date);
    }
    onStartChange(date);
  };

  // Handle end date change with validation
  const handleEndChange = (date: Date | null) => {
    if (date && startDate && date < startDate) {
      // If end date is before start date, adjust start date
      onStartChange(date);
    }
    onEndChange(date);
  };

  // Size classes
  const sizeClasses = {
    sm: {
      input: "px-2 py-1 text-sm w-[80px]",
      icon: "w-3 h-3",
      padding: "pl-6"
    },
    md: {
      input: "px-3 py-2 text-sm w-[110px]",
      icon: "w-4 h-4",
      padding: "pl-8"
    },
    lg: {
      input: "px-4 py-3 text-base w-[140px]",
      icon: "w-5 h-5",
      padding: "pl-10"
    }
  };

  const currentSize = sizeClasses[size];

  // Default input styles
  const defaultInputStyle = `
    border border-gray-300 rounded-lg 
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
    disabled:bg-gray-100 disabled:cursor-not-allowed
    ${showIcons ? currentSize.padding : 'px-3'} pr-3 py-2
    ${currentSize.input}
  `;

  return (
    <div className={`flex items-center gap-2 ${containerClassName} ${className}`}>
      {/* Start Date Picker */}
      <div className="relative">
        <DatePicker
          selected={startDate}
          onChange={handleStartChange}
          className={`${defaultInputStyle} ${inputClassName}`}
          dateFormat={dateFormat}
          placeholderText={startPlaceholder}
          maxDate={endDate || maxDate}
          minDate={minDate}
          disabled={disabled}
          calendarClassName="border-none shadow-lg"
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
        />
        {showIcons && (
          <FiCalendar 
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 ${currentSize.icon}`} 
          />
        )}
      </div>

      {/* Separator */}
      <span className="text-gray-500 text-sm">{separator}</span>

      {/* End Date Picker */}
      <div className="relative">
        <DatePicker
          selected={endDate}
          onChange={handleEndChange}
          className={`${defaultInputStyle} ${inputClassName}`}
          dateFormat={dateFormat}
          placeholderText={endPlaceholder}
          minDate={startDate || minDate}
          maxDate={maxDate}
          disabled={disabled}
          calendarClassName="border-none shadow-lg"
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
        />
        {showIcons && (
          <FiCalendar 
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 ${currentSize.icon}`} 
          />
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;

